import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Container,
  Alert,
  Skeleton,
  Stack,
  Typography,
  Box,
  Button,
  Paper,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import FlowAccordion from './FlowAccordion';
import { fetchAppFlows, runMultipleScenarios } from '../../services/appFlowsService';
import { AppiumAutomationPagesHeader } from '../appiumAutomationPagesComponents';
import useActiveScenarioStatuses from '../../hooks/useActiveScenarioStatuses';
import { useSelector } from 'react-redux';
import { selectSelectedAppBuild } from '../../redux/app/platformSlice';
import { selectMarket } from '../../redux/app/configSlice';

export default function AppFlowsPage() {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selection, setSelection] = useState({});
  const [starting, setStarting] = useState(false);
  const { getScenarioStatus, selectedDevice } = useActiveScenarioStatuses({ requireDevice: true });
  const appBuild = useSelector(selectSelectedAppBuild);
  const market = useSelector(selectMarket);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const flowsRes = await fetchAppFlows(market?.id);
        if (mounted) {
          setFlows(flowsRes || []);
          setLoading(false);
        }
      } catch (e) {
        console.error(e);
        setError('Failed to load app flows');
        setLoading(false);
      }
    }
    load();

    return () => {
      mounted = false;
    };
  }, [market?.id]);

  const totalSelected = useMemo(() => {
    return Object.values(selection).reduce((sum, ids) => sum + ids.length, 0);
  }, [selection]);

  const handleFlowSelectionChange = useCallback((flowId, scenarioIds) => {
    setSelection((prev) => ({
      ...prev,
      [flowId]: scenarioIds,
    }));
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelection({});
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!flows.length) return;

    const newSelection = {};
    flows.forEach((flow) => {
      const selectableIds = flow.scenarios
        .filter((s) => {
          const status = getScenarioStatus(flow.id, s.id);
          return status !== 'running' && status !== 'queued';
        })
        .map((s) => s.id);

      if (selectableIds.length > 0) {
        newSelection[flow.id] = selectableIds;
      }
    });

    setSelection(newSelection);
  }, [flows, getScenarioStatus]);

  const handleStartSelected = async () => {
    const items = Object.entries(selection).flatMap(([flowId, scenarioIds]) =>
      scenarioIds.map((scenarioId) => ({
        flowId,
        scenarioId,
      })),
    );

    if (!items.length) return;

    try {
      setStarting(true);
      const response = await runMultipleScenarios(items);

      if (response.invalidItems.length) {
        const errors = response.invalidItems
          .map((item) => `- ${item.flowId}::${item.scenarioId}: ${item.error}`)
          .join('\n\n');
        alert(errors);
      }

      // Allow polling to reflect running/queued before clearing selection
      setTimeout(() => {
        setSelection({});
      }, 300);
    } catch (e) {
      console.error('Failed to start scenarios', e);
      alert('Failed to start scenarios');
    } finally {
      setStarting(false);
    }
  };

  const startSelectedTooltip = !selectedDevice
    ? 'Select a device first'
    : totalSelected === 0
      ? 'Select at least one scenario to start'
      : `Start ${totalSelected} selected scenario${totalSelected !== 1 ? 's' : ''}`;

  return (
    <Container maxWidth="xl" sx={{ py: 3, pt: 0 }}>
      <AppiumAutomationPagesHeader />

      {/* Bulk actions bar */}
      {flows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Button variant="outlined" size="small" onClick={handleSelectAll}>
                Select All
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearSelection}
                disabled={totalSelected === 0}
              >
                Deselect All
              </Button>
              <Typography variant="body2" color="text.secondary">
                {totalSelected} scenario{totalSelected !== 1 ? 's' : ''} selected
              </Typography>
            </Box>

            <Tooltip title={startSelectedTooltip}>
              <span>
                <Button
                  variant="contained"
                  startIcon={starting ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                  onClick={handleStartSelected}
                  disabled={totalSelected === 0 || starting || !selectedDevice || !appBuild}
                >
                  Start Selected
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Paper>
      )}

      {loading ? (
        <Stack spacing={2}>
          <Skeleton height={60} />
          <Skeleton height={60} />
          <Skeleton height={60} />
        </Stack>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : flows.length > 0 ? (
        flows.map((flow) => (
          <FlowAccordion
            key={flow.id}
            flow={flow}
            selection={selection[flow.id] || []}
            onSelectionChange={(ids) => handleFlowSelectionChange(flow.id, ids)}
            scenarioStatuses={Object.fromEntries(
              flow.scenarios.map((s) => [s.id, getScenarioStatus(flow.id, s.id)]),
            )}
            selectedDevice={selectedDevice}
          />
        ))
      ) : (
        <Alert severity="info">No flows found</Alert>
      )}
    </Container>
  );
}
